import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Email capture for the weekly "This Week in Airlie" digest.
 * Double opt-in: submitting sends a confirmation email; user must click the
 * link in that email to actually subscribe. Anonymous, no login required.
 */
export default function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email: trimmed });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — try again");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto text-center" data-testid="newsletter-sent">
        <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-white/15 text-sponsored mb-3">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <p className="text-white/90">
          Check your inbox — we just sent a confirmation link to <span className="font-medium">{email}</span>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-md mx-auto flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
      <div className="relative flex-1">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
        <Input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={busy}
          className="pl-10 h-11 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:border-sponsored focus-visible:ring-0 rounded-full"
          data-testid="newsletter-email"
        />
      </div>
      <Button
        type="submit"
        disabled={busy}
        className="h-11 rounded-full bg-sponsored text-navy hover:bg-sponsored/90 font-medium px-6 shrink-0"
        data-testid="newsletter-submit"
      >
        {busy ? "Sending…" : "Subscribe"}
      </Button>
    </form>
  );
}
